import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks } from '../constants/theme';

export interface PlatformConfig {
  id: string;
  name: string;
  displayName: string;
  description: string;
  logo: string;
  primaryColor: string;
  secondaryColor?: string;
  url?: string;
}

interface PlatformBadgeProps {
  currentPlatform?: string;
  showPlatformToggle?: boolean;
  size?: 'sm' | 'md' | 'lg';
  placement?: 'header' | 'footer';
  className?: string;
}

// Platform configurations
const PLATFORMS: PlatformConfig[] = [
  {
    id: 'fabric',
    name: 'Microsoft Fabric',
    displayName: 'Fabric',
    description: 'Built in Microsoft Fabric',
    logo: '🏗️',
    primaryColor: '#0078D4',
    secondaryColor: '#106EBE',
    url: 'https://fabric.microsoft.com'
  },
  {
    id: 'powerbi',
    name: 'Microsoft Power BI',
    displayName: 'Power BI',
    description: 'Delivered via Power BI Enterprise',
    logo: '📊',
    primaryColor: '#F2C811',
    secondaryColor: '#D4B014',
    url: 'https://powerbi.microsoft.com'
  },
  {
    id: 'tableau',
    name: 'Tableau',
    displayName: 'Tableau',
    description: 'Powered by Tableau',
    logo: '📈',
    primaryColor: '#E97627',
    secondaryColor: '#D4661F',
    url: 'https://tableau.com'
  },
  {
    id: 'qlik',
    name: 'Qlik Sense',
    displayName: 'Qlik',
    description: 'Built with Qlik Sense',
    logo: '🔍',
    primaryColor: '#009639',
    secondaryColor: '#007A2F',
    url: 'https://qlik.com'
  },
  {
    id: 'custom',
    name: 'Custom Web Application',
    displayName: 'Custom',
    description: 'Custom Web App',
    logo: '⚡',
    primaryColor: '#6366F1',
    secondaryColor: '#4F46E5',
    url: undefined
  }
];

const BadgeContainer = styled.div<{ size: string; placement: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.size === 'sm' ? Spacing.xs : Spacing.sm}px;
  font-family: ${FontStacks.primary};
  position: relative;
  z-index: 10;
  
  ${props => props.placement === 'footer' && `
    margin-top: auto;
    padding-top: ${Spacing.sm}px;
  `}
`;

const PlatformTag = styled.div<{ platform: PlatformConfig; size: string; clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.size === 'sm' ? Spacing.xs : Spacing.sm}px;
  padding: ${props => 
    props.size === 'sm' ? `${Spacing.xs}px ${Spacing.sm}px` :
    props.size === 'md' ? `${Spacing.sm}px ${Spacing.md}px` :
    `${Spacing.md}px ${Spacing.lg}px`
  };
  background: linear-gradient(135deg, ${props => props.platform.primaryColor}15, ${props => props.platform.primaryColor}08);
  border: 1px solid ${props => props.platform.primaryColor}30;
  border-radius: ${BorderRadius.lg}px;
  font-size: ${props => 
    props.size === 'sm' ? FontSizes.xs :
    props.size === 'md' ? FontSizes.sm :
    FontSizes.md
  }px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textSecondary};
  transition: all 0.2s ease;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  text-decoration: none;
  
  &:hover {
    ${props => props.clickable && `
      background: linear-gradient(135deg, ${props.platform.primaryColor}25, ${props.platform.primaryColor}15);
      border-color: ${props.platform.primaryColor}50;
      transform: translateY(-1px);
      box-shadow: ${Shadows.sm};
    `}
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.platform.primaryColor}40;
  }
`;

const PlatformLogo = styled.span<{ size: string }>`
  font-size: ${props => 
    props.size === 'sm' ? '14px' :
    props.size === 'md' ? '16px' :
    '18px'
  };
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlatformText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-start;
`;

const PlatformName = styled.span<{ platform: PlatformConfig; size: string }>`
  font-weight: ${FontWeights.semibold};
  color: ${props => props.platform.primaryColor};
  font-size: inherit;
  line-height: 1.2;
`;

const PlatformDescription = styled.span<{ size: string }>`
  font-size: ${props => 
    props.size === 'sm' ? FontSizes.xs - 1 :
    props.size === 'md' ? FontSizes.xs :
    FontSizes.sm
  }px;
  color: ${Colors.textTertiary};
  font-weight: ${FontWeights.normal};
  line-height: 1.2;
`;

const ToggleButton = styled.button<{ size: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => 
    props.size === 'sm' ? '24px' :
    props.size === 'md' ? '28px' :
    '32px'
  };
  height: ${props => 
    props.size === 'sm' ? '24px' :
    props.size === 'md' ? '28px' :
    '32px'
  };
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  color: ${Colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${props => 
    props.size === 'sm' ? '10px' :
    props.size === 'md' ? '12px' :
    '14px'
  };
  
  &:hover {
    background: ${Colors.surfaceHover};
    border-color: ${Colors.secondary};
    color: ${Colors.secondary};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Colors.secondary}40;
  }
`;

const DropdownMenu = styled.div<{ size: string }>`
  position: absolute;
  top: calc(100% + ${Spacing.xs}px);
  right: 0;
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.lg};
  padding: ${Spacing.sm}px;
  min-width: 200px;
  z-index: 100;
  backdrop-filter: blur(8px);
`;

const DropdownItem = styled.button<{ platform: PlatformConfig; size: string; isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  width: 100%;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => props.isSelected ? `${props.platform.primaryColor}15` : 'transparent'};
  border: none;
  border-radius: ${BorderRadius.md}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    background: ${props => `${props.platform.primaryColor}20`};
    color: ${Colors.textPrimary};
  }
  
  &:focus {
    outline: none;
    background: ${props => `${props.platform.primaryColor}25`};
  }
`;

const PlatformBadge: React.FC<PlatformBadgeProps> = ({
  currentPlatform = 'fabric',
  showPlatformToggle = false,
  size = 'md',
  placement = 'footer',
  className
}) => {
  const { t } = useTranslation();
  const [selectedPlatform, setSelectedPlatform] = useState(currentPlatform);
  const [showDropdown, setShowDropdown] = useState(false);

  const platform = PLATFORMS.find(p => p.id === selectedPlatform) || PLATFORMS[0];

  const handlePlatformChange = (platformId: string) => {
    setSelectedPlatform(platformId);
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const renderPlatformTag = () => {
    const TagComponent = platform.url ? 'a' : 'div';
    const tagProps = platform.url ? {
      href: platform.url,
      target: '_blank',
      rel: 'noopener noreferrer',
      'aria-label': t('platform.visitWebsite', { platform: platform.name }, `Visit ${platform.name} website`)
    } : {};

    return (
      <PlatformTag
        as={TagComponent}
        platform={platform}
        size={size}
        clickable={!!platform.url}
        {...tagProps}
      >
        <PlatformLogo size={size}>
          {platform.logo}
        </PlatformLogo>
        <PlatformText>
          <PlatformName platform={platform} size={size}>
            {platform.displayName}
          </PlatformName>
          {size !== 'sm' && (
            <PlatformDescription size={size}>
              {t(`platform.descriptions.${platform.id}`, platform.description)}
            </PlatformDescription>
          )}
        </PlatformText>
      </PlatformTag>
    );
  };

  return (
    <BadgeContainer size={size} placement={placement} className={className}>
      {renderPlatformTag()}
      
      {showPlatformToggle && (
        <>
          <ToggleButton
            size={size}
            onClick={toggleDropdown}
            aria-label={t('platform.switchPlatform', 'Switch platform')}
            aria-expanded={showDropdown}
          >
            ⚙️
          </ToggleButton>
          
          {showDropdown && (
            <DropdownMenu size={size}>
              {PLATFORMS.map((p) => (
                <DropdownItem
                  key={p.id}
                  platform={p}
                  size={size}
                  isSelected={p.id === selectedPlatform}
                  onClick={() => handlePlatformChange(p.id)}
                >
                  <span style={{ fontSize: '16px' }}>{p.logo}</span>
                  <div>
                    <div style={{ fontWeight: FontWeights.medium, color: p.primaryColor }}>
                      {p.displayName}
                    </div>
                    <div style={{ fontSize: FontSizes.xs + 'px', color: Colors.textTertiary }}>
                      {t(`platform.descriptions.${p.id}`, p.description)}
                    </div>
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          )}
        </>
      )}
    </BadgeContainer>
  );
};

export default PlatformBadge;